import React, { useState, useCallback, memo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, User, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OnboardingSession {
  id: string;
  status: string;
  created_at: string;
}

interface OnboardingItem {
  id: string;
  status: string;
  catalog_id: string | null;
  deal_id: string | null;
  eligibility: any;
  catalog?: {
    name: string;
    description: string | null;
  } | null;
  deal?: {
    id: string;
    deal_name: string | null;
    contacts?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
    } | null;
    owner?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      display_name: string | null;
    } | null;
  } | null;
  onboarding_sessions?: OnboardingSession[];
}

interface OnboardingKanbanProps {
  items: OnboardingItem[];
  isLoading: boolean;
  onViewItem: (item: OnboardingItem) => void;
}

interface KanbanColumn {
  id: string;
  title: string;
  bgColor: string;
  borderColor: string;
  countBg: string;
  countText: string;
}

const columns: KanbanColumn[] = [
  { 
    id: 'awaiting', 
    title: 'Aguardando', 
    bgColor: 'bg-amber-50 dark:bg-amber-900/30',
    borderColor: 'border-amber-100 dark:border-amber-800',
    countBg: 'bg-amber-100 dark:bg-amber-900',
    countText: 'text-amber-700 dark:text-amber-300'
  },
  { 
    id: 'scheduled', 
    title: 'Agendado', 
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-100 dark:border-blue-800',
    countBg: 'bg-blue-100 dark:bg-blue-900',
    countText: 'text-blue-700 dark:text-blue-300'
  },
  { 
    id: 'in_progress', 
    title: 'Em Andamento', 
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
    borderColor: 'border-purple-100 dark:border-purple-800',
    countBg: 'bg-purple-100 dark:bg-purple-900',
    countText: 'text-purple-700 dark:text-purple-300'
  },
  { 
    id: 'completed', 
    title: 'Concluídos', 
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
    borderColor: 'border-emerald-100 dark:border-emerald-800',
    countBg: 'bg-emerald-100 dark:bg-emerald-900',
    countText: 'text-emerald-700 dark:text-emerald-300'
  },
];

const getLatestSession = (sessions?: OnboardingSession[]) => {
  if (!sessions || sessions.length === 0) return null;
  return sessions.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];
};

const getItemColumn = (item: OnboardingItem): string => {
  const latestSession = getLatestSession(item.onboarding_sessions);
  
  if (item.status === "pending_onboarding") {
    if (!latestSession || ['cancelled', 'completed'].includes(latestSession.status)) {
      return 'awaiting';
    }
    if (latestSession.status === 'scheduled') {
      return 'scheduled';
    }
    if (latestSession.status === 'in_progress') {
      return 'in_progress';
    }
  }
  
  if (["onboarded", "active"].includes(item.status)) {
    return 'completed';
  }
  
  return 'awaiting';
};

const getContactName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.contacts) return 'Cliente não informado';
  const { first_name, last_name } = deal.contacts;
  return [first_name, last_name].filter(Boolean).join(' ') || 'Cliente não informado';
};

const getOwnerName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.owner) return null;
  const { display_name, first_name, last_name } = deal.owner;
  return display_name || [first_name, last_name].filter(Boolean).join(' ') || null;
};

const getStatusBadge = (item: OnboardingItem) => {
  const latestSession = getLatestSession(item.onboarding_sessions);
  
  if (item.status === "pending_onboarding") {
    if (!latestSession || ['cancelled', 'completed'].includes(latestSession.status)) {
      return <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800 text-xs">Aguardando</Badge>;
    }
    if (latestSession.status === 'scheduled') {
      return <Badge className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800 text-xs">Agendado</Badge>;
    }
    if (latestSession.status === 'in_progress') {
      return <Badge className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800 text-xs">Em Andamento</Badge>;
    }
  }
  
  if (item.status === "onboarded") {
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800 text-xs">Onboarded</Badge>;
  }
  
  if (item.status === "active") {
    return <Badge className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800 text-xs">Ativo</Badge>;
  }
  
  return <Badge variant="outline" className="text-xs">{item.status}</Badge>;
};

// Memoized Card Component
const OnboardingKanbanCard = memo(({ 
  item, 
  index, 
  onView 
}: { 
  item: OnboardingItem; 
  index: number; 
  onView: () => void;
}) => {
  const ownerName = getOwnerName(item.deal);
  
  return (
    <Draggable key={item.id} draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onView}
          className={cn(
            "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow",
            snapshot.isDragging && "shadow-xl rotate-2"
          )}
        >
          <CardContent className="p-4 space-y-3">
            {/* Product Name + Badge */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                  {item.catalog?.name || 'Produto não informado'}
                </span>
              </div>
              {getStatusBadge(item)}
            </div>

            {/* Contact Name */}
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <User className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm truncate">{getContactName(item.deal)}</span>
            </div>

            {/* Owner */}
            {ownerName && (
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <User className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs truncate">{ownerName}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
});

OnboardingKanbanCard.displayName = 'OnboardingKanbanCard';

const INITIAL_VISIBLE_ITEMS = 20;
const LOAD_MORE_INCREMENT = 20;

export const OnboardingKanban: React.FC<OnboardingKanbanProps> = ({
  items,
  isLoading,
  onViewItem,
}) => {
  const [visibleCountPerColumn, setVisibleCountPerColumn] = useState<Record<string, number>>({});

  const handleDragEnd = (result: DropResult) => {
    // Drag and drop is visual only for now
    console.log('Drag ended:', result);
  };

  const getColumnItems = useCallback((columnId: string) => {
    return items.filter(item => getItemColumn(item) === columnId);
  }, [items]);

  const getVisibleItems = useCallback((columnId: string, allItems: OnboardingItem[]) => {
    const visibleCount = visibleCountPerColumn[columnId] || INITIAL_VISIBLE_ITEMS;
    return allItems.slice(0, visibleCount);
  }, [visibleCountPerColumn]);

  const loadMoreItems = useCallback((columnId: string) => {
    setVisibleCountPerColumn(prev => ({
      ...prev,
      [columnId]: (prev[columnId] || INITIAL_VISIBLE_ITEMS) + LOAD_MORE_INCREMENT
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-4">
        {columns.map((column) => (
          <div key={column.id} className="flex flex-col flex-shrink-0 w-[280px] min-w-[260px] max-w-[320px]">
            <Skeleton className="h-12 w-full rounded-t-lg" />
            <div className="space-y-3 p-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Package className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">Nenhum item encontrado</h3>
        <p className="text-muted-foreground">
          Não há itens de onboarding para exibir.
        </p>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 h-[calc(100dvh-264px)] md:h-[calc(100dvh-200px)]">
        {columns.map((column) => {
          const columnItems = getColumnItems(column.id);
          const visibleItems = getVisibleItems(column.id, columnItems);
          const hasMore = columnItems.length > visibleItems.length;

          return (
            <div key={column.id} className="flex flex-col flex-shrink-0 w-[280px] min-w-[260px] max-w-[320px] h-full">
              {/* Column Header */}
              <div className={cn(
                "border rounded-t-lg px-4 py-3 flex items-center justify-between gap-2 flex-shrink-0",
                column.bgColor,
                column.borderColor
              )}>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  {column.title}
                </h3>
                <span className={cn(
                  "px-2.5 py-0.5 rounded-full text-xs font-medium",
                  column.countBg,
                  column.countText
                )}>
                  {columnItems.length}
                </span>
              </div>

              {/* Column Content with Scroll */}
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "border-x border-b rounded-b-lg flex-1 overflow-y-auto space-y-3 p-3 transition-colors",
                      column.bgColor,
                      column.borderColor,
                      snapshot.isDraggingOver && "border-2 border-blue-400"
                    )}
                  >
                    {visibleItems.map((item, index) => (
                      <OnboardingKanbanCard
                        key={item.id}
                        item={item}
                        index={index}
                        onView={() => onViewItem(item)}
                      />
                    ))}
                    {provided.placeholder}
                    
                    {/* Load More Button */}
                    {hasMore && (
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadMoreItems(column.id)}
                          className="w-full"
                        >
                          <ChevronDown className="w-4 h-4 mr-2" />
                          Carregar mais ({columnItems.length - visibleItems.length} restantes)
                        </Button>
                      </div>
                    )}
                    
                    {/* Empty State */}
                    {columnItems.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-slate-400 dark:text-slate-500 text-sm">
                        Nenhum item
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};
